// Voice of Partner — shared constants + pure helpers used by the
// partner-facing modal, page, and form.
//
// This file used to host an in-memory mock of the active cycle +
// the partner's form-in-progress for design review. Now that all
// data flows through `feedbackApi`, only the rendering tokens and
// formatters live here. Filename is preserved (`state.js`) to keep
// existing imports stable.

// ─── Survey schema ─────────────────────────────────────────────
// Keys here MUST match the backend's CSAT_DIM_KEYS and CES_STMT_KEYS
// in feedbackController.js — anything that doesn't match is silently
// dropped on submit.
export const CSAT_DIMENSIONS = [
  { key: 'samplePickup',    name: 'Sample pickup from your facility',  desc: 'Punctuality, hygiene, phlebotomist conduct at your premises' },
  { key: 'reportTAT',       name: 'Report turnaround',                 desc: 'How fast results were delivered against committed TAT' },
  { key: 'reportQuality',   name: 'Report quality & clinical clarity', desc: 'Format, doctor-friendliness, annotations, normal-range presentation' },
  { key: 'accountManager',  name: 'Account manager responsiveness',    desc: 'How quickly your KAM responds to queries and escalations' },
  { key: 'billing',         name: 'Billing & reconciliation accuracy', desc: 'Invoicing, payment cycle, dispute resolution' },
];

export const CES_STATEMENTS = [
  { key: 'easeOfBooking',      text: 'It was easy to place orders / book tests with TrustLab this period.' },
  { key: 'easeOfCollection',   text: 'It was easy to get samples collected from our facility.' },
  { key: 'easeOfReportAccess', text: 'It was easy to access patient reports through your portal / API / WhatsApp / email.' },
];

// Used by the CSAT face row — keeps the emoji set in one place so
// changing the visual scale (e.g. dropping to 4 faces) is one edit.
const FACE_EMOJI = ['😞', '🙁', '😐', '🙂', '🤩'];
export const faceFor = (n) => ({
  emoji: FACE_EMOJI[n - 1] || '🙂',
  tooltip: `${n}/5`,
});

// Cadence label for the form banner — matches the admin side.
export const CADENCE_LABEL = {
  monthly:    'monthly',
  bimonthly:  'bi-monthly',
  quarterly:  'quarterly',
};

// ─── Pure helpers ──────────────────────────────────────────────

// "23 Apr 2026 · 11:59 PM"
export const formatCloseDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()} · 11:59 PM`;
};

// "12d 3h left" / "3h 24m left" / "24m left".
export const getCountdown = (ts) => {
  if (!ts) return '—';
  const ms = ts - Date.now();
  if (ms <= 0) return 'closing now';
  const days  = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const mins  = Math.floor((ms % 3_600_000) / 60_000);
  if (days  > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
};

// Average of the values in an object (rating set). Used by the
// thank-you state to show one number per section.
export const avgOf = (obj) => {
  const vals = Object.values(obj || {});
  if (!vals.length) return null;
  return +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
};

// 0..10 NPS score → display band.
export const npsBand = (n) =>
  n == null ? '—' : n <= 6 ? 'Detractor' : n <= 8 ? 'Passive' : 'Promoter';
